import React, { useState } from "react";
import { AlertTriangle, ShieldAlert, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

/**
 * In-app "Delete my account" flow — required for Apple App Store
 * Guideline 5.1.1(v) compliance.
 *
 * Backend: `DELETE /api/account/me` (shipped in iter67, see
 * backend/app/routes/legal.py:delete_my_account). Anonymises orders &
 * reviews, hard-deletes everything else (profile, addresses, cart,
 * wishlist, push tokens, try-on history).
 *
 * UX (Apple-reviewer friendly):
 * 1. Big visible "Delete Account" button in the Profile tab.
 * 2. Modal explains EXACTLY what's deleted (no "we'll keep some data"
 *    weasel words) and that orders are anonymised (which we MUST keep
 *    for legal / tax purposes — App Store rules explicitly allow this).
 * 3. Type-to-confirm safeguard: user must type "DELETE" to enable the
 *    final button — prevents accidental taps.
 * 4. On success: clear token, show success toast, redirect to home.
 */
export default function DeleteAccount({ "data-testid": testid = "delete-account-section" }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  // Sellers, tenant-admins and super-admins can't self-delete — they'd
  // orphan the storefront. Show a clear advisory in those roles.
  const isPrivilegedRole = user && user.role !== "customer";

  const confirmEnabled = typed.trim().toUpperCase() === "DELETE" && !busy;

  const handleDelete = async () => {
    if (!confirmEnabled) return;
    setBusy(true);
    try {
      await api.delete("/account/me", { data: { confirm: true } });
      toast.success("Your account has been deleted.");
      // Wipe local session + token.
      try { localStorage.removeItem("kb_token"); } catch { /* ignore */ }
      try { localStorage.removeItem("kb_user"); } catch { /* ignore */ }
      logout?.();
      // Redirect to home (storefront landing) — login modal opens from there.
      setTimeout(() => navigate("/", { replace: true }), 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Account deletion failed";
      toast.error(typeof msg === "string" ? msg : "Account deletion failed");
    } finally {
      setBusy(false);
    }
  };

  if (isPrivilegedRole) {
    return (
      <div
        data-testid={testid}
        className="mt-10 border border-amber-200 bg-amber-50 p-4 rounded text-sm"
      >
        <div className="flex items-start gap-2">
          <ShieldAlert size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900">
              Account deletion not available for {user.role}s
            </div>
            <div className="text-xs text-amber-800 mt-1 leading-relaxed">
              Your account is tied to an active storefront. Please contact
              platform support to request deletion — we&apos;ll transfer
              ownership or wind down the store before erasing your data.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testid} className="mt-10 pt-6 border-t border-[hsl(var(--kb-border))]">
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-display text-lg text-red-700" data-testid="delete-account-heading">
            Delete Account
          </h3>
          <p className="text-xs text-[hsl(var(--kb-muted))] leading-relaxed mt-1 max-w-lg">
            Permanently erase your personal data — profile, saved addresses,
            cart, wishlist, push tokens, measurements, and try-on history.
            Your account will be marked deleted and you won&apos;t be able to
            sign back in. <b>This action cannot be undone.</b>
          </p>
          <p className="text-[11px] text-[hsl(var(--kb-muted))] leading-relaxed mt-2 max-w-lg">
            For legal, tax and warranty obligations, past orders, payments,
            invoices and delivery records are retained but fully anonymised
            (your name, phone and email are removed and replaced with an
            opaque identifier so they can never be linked back to you).
          </p>
        </div>
      </div>
      <button
        onClick={() => { setTyped(""); setModalOpen(true); }}
        className="mt-2 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
        data-testid="delete-account-btn"
      >
        Delete my account
      </button>

      <Dialog open={modalOpen} onOpenChange={(o) => { if (!busy) setModalOpen(o); }}>
        <DialogContent data-testid="delete-account-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} /> Permanently delete account?
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed pt-2">
              We will immediately erase your profile, addresses, cart, wishlist,
              push notification tokens, measurements, and try-on history, and
              flag your account as deleted so you can&apos;t sign back in.
              Past orders, payments and invoices will be anonymised (kept on
              file for legal/tax reasons, but no longer linked to you).
              <b> This cannot be undone.</b>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <label className="block text-xs text-[hsl(var(--kb-muted))]">
              Type <code className="font-mono bg-[hsl(var(--kb-surface-alt))] px-1 py-0.5 rounded text-[11px]">DELETE</code> to confirm:
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              placeholder="DELETE"
              className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-red-600 rounded"
              disabled={busy}
              data-testid="delete-account-confirm-input"
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              disabled={busy}
              className="kb-btn-outline text-sm w-full sm:w-auto"
              data-testid="delete-account-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!confirmEnabled}
              className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed text-white rounded transition-colors inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              data-testid="delete-account-confirm-btn"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {busy ? "Deleting…" : "Yes, permanently delete"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
